import { FunctionComponent, JSX } from 'react'
import { useMutation } from '@apollo/client'
import { Check, Close } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { RESPOND_TO_CONSENT_REQUEST } from '@/modules/API/parent/familyConsentHandler'
import { FamilyConsentRequest } from '@/types/FamilyConsent'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import styles from './incomingRequests.module.scss'

type IncomingRequestsProps = {
    requests: FamilyConsentRequest[]
    daycareId: number
    onUpdate: () => void
}

const IncomingRequests: FunctionComponent<IncomingRequestsProps> = ({
    requests,
    daycareId,
    onUpdate
}: IncomingRequestsProps) => {
    const { t } = useTranslation()

    const [respond, { loading }] = useMutation(RESPOND_TO_CONSENT_REQUEST, {
        onCompleted: onUpdate
    })

    function handleDecision(consentRequestId: number, decision: 'accepted' | 'refused'): void {
        void respond({ variables: { daycareId, consentRequestId, decision } })
    }

    if (requests.length === 0) {
        return <p className={styles.Empty}>{t('noIncomingConsentRequests')}</p>
    }

    return (
        <div className={styles.List}>
            {requests.map(
                (request: FamilyConsentRequest): JSX.Element => (
                    <div key={request.id} className={styles.Card}>
                        <div className={styles.TextContainer}>
                            <p className={styles.FamilyName}>{request.parentName}</p>
                            <p className={styles.RequestedBy}>
                                {t('consentRequestedBy')} {request.requestingDaycareName}
                            </p>
                        </div>
                        <div className={styles.Actions}>
                            <ButtonTextIcon
                                type='custom'
                                customIcon={<Check />}
                                customText={t('accept')}
                                onClick={() => handleDecision(request.id, 'accepted')}
                                disabled={loading}
                            />
                            <ButtonTextIcon
                                type='custom'
                                customIcon={<Close />}
                                customText={t('refuse')}
                                onClick={() => handleDecision(request.id, 'refused')}
                                disabled={loading}
                            />
                        </div>
                    </div>
                )
            )}
        </div>
    )
}

export default IncomingRequests
